import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  BanknotesIcon,
  ArrowsRightLeftIcon,
  ChartBarIcon,
  CurrencyDollarIcon,
  ClockIcon,
  ShieldCheckIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  CalculatorIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const LiquidityMiningPage: React.FC = () => {
  const pools = [
    {
      name: 'NDH-MATIC',
      apy: '125%',
      tvl: '$12.5M',
      rewards: 'NDH',
      risk: 'Medium',
      icon: '🟣'
    },
    {
      name: 'NDH-USDC',
      apy: '85%',
      tvl: '$8.2M',
      rewards: 'NDH',
      risk: 'Low',
      icon: '🔵'
    },
    {
      name: 'NDH-ETH',
      apy: '110%',
      tvl: '$5.8M',
      rewards: 'NDH',
      risk: 'Medium',
      icon: '🔷'
    },
    {
      name: 'NDH Single Stake',
      apy: '45%',
      tvl: '$25M',
      rewards: 'NDH',
      risk: 'Low',
      icon: '✨'
    }
  ];

  const benefits = [
    {
      icon: CurrencyDollarIcon,
      title: 'Earn Passive Income',
      description: 'Provide liquidity and earn NDH rewards automatically distributed to your wallet'
    },
    {
      icon: ArrowsRightLeftIcon,
      title: 'Trading Fee Share',
      description: 'Earn a portion of trading fees generated by the liquidity pool'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Audited Contracts',
      description: 'All liquidity mining contracts are thoroughly audited for security'
    },
    {
      icon: ClockIcon,
      title: 'Flexible Withdrawal',
      description: 'No lock-up periods - withdraw your liquidity anytime'
    }
  ];

  const howItWorks = [
    {
      step: 1,
      title: 'Choose a Pool',
      description: 'Select a liquidity pool based on your risk tolerance and reward preferences'
    },
    {
      step: 2,
      title: 'Provide Liquidity',
      description: 'Deposit equal value of both tokens (e.g., NDH + MATIC) into the pool'
    },
    {
      step: 3,
      title: 'Receive LP Tokens',
      description: 'Get LP tokens representing your share of the pool'
    },
    {
      step: 4,
      title: 'Stake LP Tokens',
      description: 'Stake your LP tokens in the farming contract to earn NDH rewards'
    },
    {
      step: 5,
      title: 'Harvest Rewards',
      description: 'Claim your accumulated NDH rewards anytime or auto-compound'
    }
  ];

  const stats = [
    { label: 'Total Value Locked', value: '$51.5M' },
    { label: 'Total Rewards Distributed', value: '25M NDH' },
    { label: 'Average APY', value: '91%' },
    { label: 'Active Farmers', value: '15,000+' },
  ];

  return (
    <div className="min-h-screen bg-dark-950 pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-b from-dark-900 to-dark-950 overflow-hidden">
        <div className="absolute inset-0 bg-[url('/grid-pattern.svg')] opacity-5" />
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[600px] h-[600px] bg-green-500/10 rounded-full blur-3xl" />
        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-green-500/20 rounded-2xl">
                <BanknotesIcon className="w-12 h-12 text-green-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Liquidity <span className="text-green-400">Mining</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-8">
              Provide liquidity to NDH pairs and earn generous rewards. Support the ecosystem while growing your holdings.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="#pools"
                className="px-8 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
              >
                Start Farming
              </a>
              <Link
                to="/docs#liquidity"
                className="px-8 py-3 bg-dark-700 hover:bg-dark-600 text-white rounded-xl transition-colors"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-dark-900/50 border-y border-dark-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-green-400 mb-2">{stat.value}</div>
                <div className="text-gray-400 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Why Provide Liquidity?</h2>
              <p className="text-gray-400">Multiple ways to earn while supporting the ecosystem</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-dark-800/50 rounded-xl p-6 border border-dark-700"
                  >
                    <Icon className="w-10 h-10 text-green-400 mb-4" />
                    <h3 className="text-lg font-semibold text-white mb-2">{benefit.title}</h3>
                    <p className="text-gray-400 text-sm">{benefit.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Active Pools */}
      <section id="pools" className="py-16 bg-dark-900/30 scroll-mt-24">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Active Pools</h2>
              <p className="text-gray-400">Choose a pool and start earning rewards</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-dark-700">
                    <th className="text-left py-4 px-4 text-gray-400 font-medium">Pool</th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium">APY</th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium">TVL</th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium">Rewards</th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium">Risk</th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {pools.map((pool, idx) => (
                    <motion.tr
                      key={idx}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      className="border-b border-dark-700 hover:bg-dark-800/50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-3">
                          <span className="text-2xl">{pool.icon}</span>
                          <span className="text-white font-semibold">{pool.name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-green-400 font-bold">{pool.apy}</span>
                      </td>
                      <td className="py-4 px-4 text-right text-white">{pool.tvl}</td>
                      <td className="py-4 px-4 text-right">
                        <span className="px-2 py-1 bg-primary-500/20 text-primary-400 text-sm rounded">
                          {pool.rewards}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className={`px-2 py-1 text-sm rounded ${
                          pool.risk === 'Low' ? 'bg-green-500/20 text-green-400' :
                          pool.risk === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>
                          {pool.risk}
                        </span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <button className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors text-sm">
                          Farm
                        </button>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">How It Works</h2>
              <p className="text-gray-400">Simple steps to start earning rewards</p>
            </div>
            <div className="space-y-6">
              {howItWorks.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: idx % 2 === 0 ? -20 : 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="flex gap-6"
                >
                  <div className="flex flex-col items-center">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center text-green-400 font-bold">
                      {step.step}
                    </div>
                    {idx < howItWorks.length - 1 && (
                      <div className="w-0.5 h-full bg-dark-700 mt-2" />
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <h3 className="text-xl font-semibold text-white mb-2">{step.title}</h3>
                    <p className="text-gray-400">{step.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 bg-dark-900/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-dark-800/50 rounded-2xl p-8 border border-dark-700">
              <div className="flex items-center gap-3 mb-6">
                <CalculatorIcon className="w-8 h-8 text-green-400" />
                <h2 className="text-2xl font-bold text-white">Rewards Calculator</h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Deposit Amount (USD)</label>
                  <input
                    type="number"
                    placeholder="1000"
                    className="w-full px-4 py-3 bg-dark-900 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Pool APY (%)</label>
                  <input
                    type="number"
                    placeholder="100"
                    className="w-full px-4 py-3 bg-dark-900 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Duration (Days)</label>
                  <input
                    type="number"
                    placeholder="365"
                    className="w-full px-4 py-3 bg-dark-900 border border-dark-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-green-500"
                  />
                </div>
              </div>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Estimated Rewards</span>
                  <span className="text-2xl font-bold text-green-400">~$1,000 NDH</span>
                </div>
              </div>
              <p className="text-gray-500 text-xs mt-4">
                * Estimated rewards based on current APY. Actual rewards may vary.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Risks */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <InformationCircleIcon className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-yellow-400 mb-2">Understanding Risks</h3>
                  <ul className="space-y-2 text-gray-300 text-sm">
                    <li>• <strong>Impermanent Loss:</strong> Price divergence between pool tokens can result in less value than holding</li>
                    <li>• <strong>Smart Contract Risk:</strong> While audited, smart contracts may have undiscovered vulnerabilities</li>
                    <li>• <strong>Token Price Volatility:</strong> Reward tokens may fluctuate in value</li>
                    <li>• <strong>APY Variability:</strong> Displayed APYs are estimates and can change based on pool participation</li>
                  </ul>
                  <Link to="/docs#lp-risks" className="text-yellow-400 text-sm hover:underline mt-3 inline-block">
                    Learn more about liquidity provision risks →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-green-500/20 to-emerald-500/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <ArrowTrendingUpIcon className="w-12 h-12 text-green-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">
              Ready to Start Farming?
            </h2>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Connect your wallet and start earning NDH rewards today. No minimum deposit required.
            </p>
            <a
              href="#pools"
              className="inline-flex items-center gap-2 px-8 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl transition-colors"
            >
              <SparklesIcon className="w-5 h-5" />
              Start Earning Now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LiquidityMiningPage;
